import type { Questionnaire, QuestionnaireItem } from "@medplum/fhirtypes";

export interface MissingQuestionnaireAnswerOptionRepairSummary {
  humanizedOptionCount: number;
  removedPlaceholderOptionCount: number;
  affectedItemLinkIds: string[];
}

export interface MissingQuestionnaireAnswerOptionRepairResult {
  questionnaire: Questionnaire;
  summary: MissingQuestionnaireAnswerOptionRepairSummary;
}

interface LegacyAnswerOptionRepairSummary {
  repairedItemLinkIds: string[];
  repairedOptionCount: number;
  repairedQuestionCount: number;
}

export interface LegacyQuestionnaireAnswerOptionRepairResult {
  questionnaire: Questionnaire;
  summary: LegacyAnswerOptionRepairSummary;
}

function hasWhitespace({ value }: { value: string }): boolean {
  return /\s/.test(value);
}

function hasDefinedAnswerOptionValue({
  answerOption,
}: {
  answerOption: NonNullable<QuestionnaireItem["answerOption"]>[number];
}): boolean {
  return Object.entries(answerOption).some(
    ([key, value]) => key.startsWith("value") && value !== undefined && value !== "",
  );
}

function isUuid({ value }: { value: string }): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

export const LegacyAnswerOptionClassificationKind = {
  LabelCoded: "label-coded",
  WhitespaceCode: "whitespace-code",
  MachineSystemlessCode: "machine-systemless-code",
} as const;

export type LegacyAnswerOptionClassificationKind =
  (typeof LegacyAnswerOptionClassificationKind)[keyof typeof LegacyAnswerOptionClassificationKind];

export interface LegacyAnswerOptionClassification {
  kind: LegacyAnswerOptionClassificationKind;
  isRepairable: boolean;
}

/**
 * Classifies systemless codings for both repair and audit flows.
 *
 * Audit still wants to bucket whitespace codes even when the display is distinct,
 * while repair only rewrites the narrow subset that safely mirrors the label.
 */
export function classifyLegacyAnswerOption({
  answerOption,
}: {
  answerOption: NonNullable<QuestionnaireItem["answerOption"]>[number];
}): LegacyAnswerOptionClassification | undefined {
  if (!answerOption.valueCoding || answerOption.valueString !== undefined) {
    return undefined;
  }

  const { system, code, display } = answerOption.valueCoding;
  if (system || !code) {
    return undefined;
  }

  if (display === code) {
    return {
      kind: LegacyAnswerOptionClassificationKind.LabelCoded,
      isRepairable: true,
    };
  }

  if (hasWhitespace({ value: code })) {
    return {
      kind: LegacyAnswerOptionClassificationKind.WhitespaceCode,
      isRepairable: display === undefined || display === "" || display === code,
    };
  }

  return {
    kind: LegacyAnswerOptionClassificationKind.MachineSystemlessCode,
    isRepairable: false,
  };
}

/**
 * Humanizes stable legacy option ids like `workPhone` or `not_selected`.
 *
 * UUID-only placeholders are intentionally ignored because they carry no safe
 * user-facing value and should be dropped instead of guessed.
 */
export function humanizeLegacyAnswerOptionId({
  optionId,
}: {
  optionId: string;
}): string | undefined {
  const trimmedOptionId = optionId.trim();
  if (!trimmedOptionId || isUuid({ value: trimmedOptionId })) {
    return undefined;
  }

  const spacedValue = trimmedOptionId
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();
  if (!spacedValue) {
    return undefined;
  }

  return spacedValue
    .split(/\s+/)
    .map((token) => {
      if (token.length <= 1) {
        return token.toUpperCase();
      }

      if (/^[A-Z0-9]+$/.test(token) && token.length <= 4) {
        return token;
      }

      return token[0].toUpperCase() + token.slice(1).toLowerCase();
    })
    .join(" ");
}

/**
 * Repairs id-only answer options that predate stricter builder persistence.
 *
 * - Stable ids like `workPhone` become `valueString: "Work Phone"`
 * - UUID-only placeholders are dropped because they have no recoverable value
 */
export function repairMissingAnswerOptionValue({
  answerOption,
}: {
  answerOption: NonNullable<QuestionnaireItem["answerOption"]>[number];
}): NonNullable<QuestionnaireItem["answerOption"]>[number] | undefined {
  if (hasDefinedAnswerOptionValue({ answerOption })) {
    return answerOption;
  }

  const optionId = answerOption.id?.trim();
  if (!optionId) {
    return undefined;
  }

  const humanizedValue = humanizeLegacyAnswerOptionId({ optionId });
  if (!humanizedValue) {
    return undefined;
  }

  return {
    ...answerOption,
    valueString: humanizedValue,
  };
}

interface QuestionnaireItemMissingAnswerOptionRepairResult {
  item: QuestionnaireItem;
  humanizedOptionCount: number;
  removedPlaceholderOptionCount: number;
  affectedItemLinkIds: string[];
}

function repairQuestionnaireItemMissingAnswerOptionValues({
  item,
}: {
  item: QuestionnaireItem;
}): QuestionnaireItemMissingAnswerOptionRepairResult {
  const rebuiltItem = { ...item };
  delete rebuiltItem.answerOption;
  delete rebuiltItem.item;
  let humanizedOptionCount = 0;
  let removedPlaceholderOptionCount = 0;
  const affectedItemLinkIds = new Set<string>();

  const repairedAnswerOption = item.answerOption?.flatMap((answerOption) => {
    if (hasDefinedAnswerOptionValue({ answerOption })) {
      return [answerOption];
    }

    const repairedAnswerOption = repairMissingAnswerOptionValue({ answerOption });
    if (!repairedAnswerOption) {
      removedPlaceholderOptionCount += 1;
      affectedItemLinkIds.add(item.linkId);
      return [];
    }

    humanizedOptionCount += 1;
    affectedItemLinkIds.add(item.linkId);
    return [repairedAnswerOption];
  });

  const repairedChildren = item.item?.map((childItem) =>
    repairQuestionnaireItemMissingAnswerOptionValues({ item: childItem }),
  );

  repairedChildren?.forEach((childResult) => {
    humanizedOptionCount += childResult.humanizedOptionCount;
    removedPlaceholderOptionCount += childResult.removedPlaceholderOptionCount;
    childResult.affectedItemLinkIds.forEach((itemLinkId) => {
      affectedItemLinkIds.add(itemLinkId);
    });
  });

  return {
    item: {
      ...rebuiltItem,
      ...(repairedAnswerOption?.length ? { answerOption: repairedAnswerOption } : {}),
      ...(repairedChildren
        ? { item: repairedChildren.map((childResult) => childResult.item) }
        : {}),
    },
    humanizedOptionCount,
    removedPlaceholderOptionCount,
    affectedItemLinkIds: Array.from(affectedItemLinkIds),
  };
}

/**
 * Repairs malformed id-only answer options across a Questionnaire tree.
 */
export function repairMissingQuestionnaireAnswerOptionValues({
  questionnaire,
}: {
  questionnaire: Questionnaire;
}): MissingQuestionnaireAnswerOptionRepairResult {
  const affectedItemLinkIds = new Set<string>();
  let humanizedOptionCount = 0;
  let removedPlaceholderOptionCount = 0;

  const repairedItems = questionnaire.item?.map((item) => {
    const result = repairQuestionnaireItemMissingAnswerOptionValues({ item });
    humanizedOptionCount += result.humanizedOptionCount;
    removedPlaceholderOptionCount += result.removedPlaceholderOptionCount;
    result.affectedItemLinkIds.forEach((itemLinkId) => {
      affectedItemLinkIds.add(itemLinkId);
    });
    return result.item;
  });

  return {
    questionnaire: {
      ...questionnaire,
      ...(repairedItems ? { item: repairedItems } : {}),
    },
    summary: {
      humanizedOptionCount,
      removedPlaceholderOptionCount,
      affectedItemLinkIds: Array.from(affectedItemLinkIds),
    },
  };
}

/**
 * Restrict legacy option repair to high-confidence builder bugs.
 *
 * We only rewrite systemless codings when the code clearly mirrors the label
 * or when a whitespace-only code has no distinct display to preserve.
 */
export function shouldRepairLegacyAnswerOption({
  answerOption,
}: {
  answerOption: NonNullable<QuestionnaireItem["answerOption"]>[number];
}): boolean {
  return classifyLegacyAnswerOption({ answerOption })?.isRepairable ?? false;
}

/**
 * Converts a legacy builder-style `valueCoding` answer option into `valueString`.
 */
export function repairLegacyAnswerOption({
  answerOption,
}: {
  answerOption: NonNullable<QuestionnaireItem["answerOption"]>[number];
}): NonNullable<QuestionnaireItem["answerOption"]>[number] {
  if (!shouldRepairLegacyAnswerOption({ answerOption })) {
    return answerOption;
  }

  const normalizedValue = answerOption.valueCoding?.display || answerOption.valueCoding?.code;
  if (!normalizedValue) {
    return answerOption;
  }

  const { valueCoding: _valueCoding, ...rest } = answerOption;
  return {
    ...rest,
    valueString: normalizedValue,
  };
}

/**
 * Normalizes answer options for builder persistence.
 *
 * Order matters:
 * - first recover/drop malformed id-only placeholders
 * - then repair the older label-as-code builder bug
 */
export function normalizeAnswerOptionForPersistence({
  answerOption,
}: {
  answerOption: NonNullable<QuestionnaireItem["answerOption"]>[number];
}): NonNullable<QuestionnaireItem["answerOption"]>[number] | undefined {
  const repairedMissingValue = repairMissingAnswerOptionValue({ answerOption });
  if (!repairedMissingValue) {
    return undefined;
  }

  return repairLegacyAnswerOption({ answerOption: repairedMissingValue });
}

interface QuestionnaireItemRepairResult {
  item: QuestionnaireItem;
  repairedItemLinkIds: string[];
  repairedOptionCount: number;
}

function repairQuestionnaireItemAnswerOptions({
  item,
}: {
  item: QuestionnaireItem;
}): QuestionnaireItemRepairResult {
  const rebuiltItem = { ...item };
  delete rebuiltItem.answerOption;
  delete rebuiltItem.item;
  const repairedItemLinkIds = new Set<string>();
  let repairedOptionCount = 0;

  const repairedAnswerOption = item.answerOption?.map((answerOption) => {
    const classification = classifyLegacyAnswerOption({ answerOption });
    if (!classification?.isRepairable) {
      return answerOption;
    }

    repairedOptionCount += 1;
    repairedItemLinkIds.add(item.linkId);
    return repairLegacyAnswerOption({ answerOption });
  });

  const repairedChildren = item.item?.map((childItem) =>
    repairQuestionnaireItemAnswerOptions({ item: childItem }),
  );

  repairedChildren?.forEach((childResult) => {
    repairedOptionCount += childResult.repairedOptionCount;
    childResult.repairedItemLinkIds.forEach((itemLinkId) => {
      repairedItemLinkIds.add(itemLinkId);
    });
  });

  return {
    item: {
      ...rebuiltItem,
      ...(repairedAnswerOption?.length ? { answerOption: repairedAnswerOption } : {}),
      ...(repairedChildren
        ? { item: repairedChildren.map((childResult) => childResult.item) }
        : {}),
    },
    repairedItemLinkIds: Array.from(repairedItemLinkIds),
    repairedOptionCount,
  };
}

/**
 * Repairs only the safe subset of legacy builder answer options on a Questionnaire.
 */
export function repairLegacyQuestionnaireAnswerOptions({
  questionnaire,
}: {
  questionnaire: Questionnaire;
}): LegacyQuestionnaireAnswerOptionRepairResult {
  const repairedItemLinkIds = new Set<string>();
  let repairedOptionCount = 0;

  const repairedItems = questionnaire.item?.map((item) => {
    const result = repairQuestionnaireItemAnswerOptions({ item });
    repairedOptionCount += result.repairedOptionCount;
    result.repairedItemLinkIds.forEach((itemLinkId) => {
      repairedItemLinkIds.add(itemLinkId);
    });
    return result.item;
  });

  return {
    questionnaire: {
      ...questionnaire,
      ...(repairedItems ? { item: repairedItems } : {}),
    },
    summary: {
      repairedItemLinkIds: Array.from(repairedItemLinkIds),
      repairedOptionCount,
      repairedQuestionCount: repairedItemLinkIds.size,
    },
  };
}
